import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Check } from "lucide-react";

/**
 * Celebration overlay shown after a player unlocks (buys) an avatar.
 * Styled to match FirstMatchCelebration/daily card: centered spring-in card
 * with the avatar image, name, its passive bonus and an "Equipped!" badge.
 * Auto-dismisses after ~3s; tap anywhere to close early.
 *
 * Props: open, avatar {name, bonus, img}, onDone
 */
export default function AvatarUnlockCelebration({ open, avatar, onDone }) {
  const onDoneRef = React.useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => onDoneRef.current?.(), 3000);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <AnimatePresence>
      {open && avatar && (
        <div
          data-testid="avatar-unlock-celebration"
          className="fixed inset-0 z-[200] flex items-center justify-center px-4 bg-[#2C332A]/40 backdrop-blur-[2px]"
          aria-live="polite"
          onClick={() => onDoneRef.current?.()}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className="relative w-full max-w-[340px] rounded-3xl overflow-hidden shadow-2xl px-6 py-7 text-center"
            style={{
              background: "linear-gradient(135deg,#8AA48F 0%,#5F7161 55%,#47564A 100%)",
              boxShadow: "0 20px 60px rgba(44,51,42,0.55), inset 0 2px 0 rgba(255,255,255,0.35)",
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0.5 }}
              animate={{ scale: 1.05, opacity: 0.9 }}
              transition={{ duration: 1.2, repeat: 1, repeatType: "reverse" }}
              className="absolute inset-0 rounded-3xl pointer-events-none"
              style={{ background: "radial-gradient(ellipse at top, rgba(255,255,255,0.5), transparent 65%)" }}
            />
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 0, y: 0, scale: 0.6 }}
                animate={{ opacity: [0, 1, 0], x: (i % 2 === 0 ? -1 : 1) * (30 + i * 14), y: -20 - i * 12, scale: 1, rotate: 25 * (i - 2) }}
                transition={{ duration: 1.6, delay: 0.1 + i * 0.12 }}
                className="absolute top-6 left-1/2 -translate-x-1/2 text-white pointer-events-none"
              >
                <Sparkles size={16} strokeWidth={2.2} />
              </motion.div>
            ))}

            <p className="relative text-[11px] uppercase tracking-[0.2em] font-bold text-white/80 mb-3">Avatar unlocked</p>
            <motion.div
              initial={{ rotate: -18, scale: 0.6 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 240, damping: 14, delay: 0.05 }}
              className="relative w-28 h-28 mx-auto rounded-full bg-cover bg-center border-[3px] border-white shadow-lg"
              style={{ backgroundImage: `url(${avatar.img})` }}
            />
            <p style={{ fontFamily: "Outfit, sans-serif" }} className="relative text-2xl font-semibold text-white mt-3">{avatar.name}</p>
            {avatar.bonus && (
              <p className="relative text-xs text-white/90 mt-1 flex items-center justify-center gap-1">
                <Sparkles size={11} strokeWidth={1.8} />
                {avatar.bonus}
              </p>
            )}
            <div className="relative mt-4 inline-flex items-center gap-1.5 bg-white/20 rounded-full px-4 py-1.5 text-white font-semibold text-sm">
              <Check size={15} strokeWidth={2.6} />
              Equipped!
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
